<template>
	<div
		:class="{
			'pdf-viewer-lazy-load-container': true,
			'is-loading': isRendering,
		}"
	>
		<LoaderComponent v-show="isRendering" />

		<PDFViewer
			v-bind="props"
			@pdfLoaded="onPdfLoaded"
			@rendered="onRender"
			@error="onError"
		/>

		<span
			v-if="hasRenderError"
			class="error-message fade-in-fast"
		>
			<i class="fas fa-exclamation-circle" /> An error occurred while trying to load the PDF.
		</span>
	</div>
</template>
<script setup lang="ts">
	import { ref } from 'vue';

	import PDFViewer, { IPDFViewerProps } from '@core/components/PDFViewer.vue';
	import LoaderComponent from '@core/components/UI/LoaderComponent.vue';
	import { PDFDocumentProxy } from 'pdfjs-dist';

	const props = defineProps<IPDFViewerProps>();
	const emit = defineEmits<{ pageRendered: []; pdfLoaded: [PDFDocumentProxy | any] }>();

	const isRendering = ref(true);
	const hasRenderError = ref(false);

	const onPdfLoaded = (pdf: PDFDocumentProxy) => {
		emit('pdfLoaded', pdf);
	};

	const onRender = () => {
		isRendering.value = false;
		emit('pageRendered');
	};

	/** Encountered when any page in the PDF encounters an error while trying to render */
	const onError = () => {
		hasRenderError.value = true;
	};
</script>
<style>
	.pdf-viewer-lazy-load-container {
		position: relative;
	}

	.pdf-viewer-lazy-load-container.is-loading {
		position: relative;
		border-radius: var(--default-border-radius);
		background-color: var(--border-color);
		animation: loadingAnimation 1.3s infinite;
	}

	.pdf-viewer-lazy-load-container.is-loading .spinner-container {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		font-size: 2.5rem;
	}

	.pdf-viewer-lazy-load-container .error-message {
		position: absolute;
		top: 0;
		left: 50%;
		width: fit-content;
		padding: 5px 10px;
		border-radius: var(--default-border-radius);
		color: var(--button-color);
		background-color: var(--error-color);
		transform: translateX(-50%);
	}

	@keyframes loadingAnimation {
		0% {
			opacity: 1;
		}

		70% {
			opacity: 0.7;
		}

		100% {
			opacity: 1;
		}
	}
</style>
